import { t } from "ttag";

import ActionButton from "metabase/common/components/ActionButton";
import { useToast } from "metabase/common/hooks";
import { useSelector } from "metabase/lib/redux";
import { getUserIsAdmin } from "metabase/selectors/user";
import { Button, Group, Modal, Progress, Stack, Text } from "metabase/ui";
import { useCancelRemoteSyncCurrentTaskMutation } from "metabase-enterprise/api";
import type { RemoteSyncTaskType } from "metabase-types/api";

interface SyncProgressModalProps {
  taskType: RemoteSyncTaskType;
  progress: number;
  isError: boolean;
  errorMessage: string;
  onDismiss: () => void;
}

export function SyncProgressModal({
  progress,
  taskType,
  isError,
  errorMessage,
  onDismiss,
}: SyncProgressModalProps) {
  const canCancel = useSelector(getUserIsAdmin);

  const [cancelRemoteSyncCurrentTask] =
    useCancelRemoteSyncCurrentTaskMutation();
  const [sendToast] = useToast();

  const onCancel = async () => {
    try {
      await cancelRemoteSyncCurrentTask().unwrap();
      onDismiss();
    } catch (error: any) {
      let message = t`Failed to cancel sync`;

      if (typeof error?.data === "string") {
        message += `: ${error.data}`;
      }

      sendToast({
        message,
        icon: "warning",
        toastColor: "error",
      });

      if (message.match(/no active task/i)) {
        onDismiss();
      }

      throw error;
    }
  };

  if (isError) {
    return (
      <Modal onClose={onDismiss} opened size="md" title={t`Sync failed`}>
        <Stack mt="md" gap="md">
          <Text>{t`An error occurred during sync.`}</Text>
          {errorMessage && <Text>{errorMessage}</Text>}
          <Group justify="flex-end">
            <Button
              data-testid="sync-error-close-button"
              onClick={onDismiss}
              variant="filled"
            >{t`Close`}</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  const { title, progressLabel } = getModalContent(taskType);

  return (
    <Modal
      onClose={onDismiss}
      opened
      size="md"
      title={title}
      withCloseButton={false}
    >
      <Stack mt="md" gap="md">
        <Text ta="center">{progressLabel}</Text>
        <Progress value={progress * 100} transitionDuration={300} animated />
        <Text size="sm">
          {t`Please wait until this finishes before editing content.`}
        </Text>
        {canCancel && (
          <Group justify="flex-end">
            <ActionButton
              actionFn={onCancel}
              normalText={t`Cancel`}
              activeText={t`Cancelling…`}
              failedText={t`Cancel`}
            />
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

const getModalContent = (
  taskType: RemoteSyncTaskType,
): { title: string; progressLabel: string } => {
  if (taskType === "import") {
    return {
      title: t`Pulling from Git`,
      progressLabel: t`Importing content…`,
    };
  }

  return {
    title: t`Pushing to Git`,
    progressLabel: t`Exporting content…`,
  };
};
